import {
  type ExpenseResource,
  ExpensesController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
  type VendorResource,
  VendorsController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconPaperclip, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { currentCursor, type CursorTrail, FIRST_PAGE } from '../back-office/cursor-pagination';
import { CursorPager } from '../back-office/CursorPager';
import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import { labelForPaymentMode } from '../sales/payment-modes';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { ExpenseAttachmentsDialog } from './ExpenseAttachmentsDialog';
import { defaultPeriod, periodProblem, type ReportingPeriod } from './reporting-period';

/**
 * What money left the outlets for, and when: `GET /v1/expenses`.
 *
 * **The first screen of Reporting**, and the read half of FE049. The write is
 * `RecordExpense` beside it, reached from this screen's one action.
 *
 * ## The period is required, and that is the shape of this screen
 *
 * `from` and `to` are not optional filters here the way `businessDate` is on the
 * tickets list — the route takes both or refuses. So the screen opens on a
 * period rather than on everything, and `reporting-period.ts` holds the default,
 * the Asia/Kolkata trading day it ends on, and the three ways a range is
 * refused. Read its note before changing any of that; the clock question it
 * answers is the one `business-date.ts` deliberately declined to.
 *
 * **An expense falls in the period its outlet's business day falls in**, not the
 * instant it was recorded at — the document says so on the `from` parameter, so
 * that this list and a profit and loss over one period cannot disagree about
 * which day something happened on. The table therefore leads with the trading
 * day, which is the column the filter actually matches.
 *
 * ## Paged by cursor, and not by offset
 *
 * `ExpensePageResource` is `{ items, limit, nextCursor }` with **no total**, so
 * `Pager` and the sums in `pagination.ts` do not fit — `cursor-pagination.ts`
 * beside them is the keyset version and says why it is a separate module. The
 * cash book FE050 reads is paged the same way and takes the same pair.
 *
 * **Nothing here retries paging automatically, deliberately.**
 * `blossom-rest-api#347` has an out-of-range cursor instant answering a
 * *retryable* `500` rather than a coded refusal, which is exactly what a sync
 * loop would spin on. Paging is a button somebody presses, and the failure
 * notice offers one retry rather than taking one.
 *
 * ## What is deliberately not computed
 *
 * No total, and no sum down the amount column. `docs/ui-conventions.md`: the
 * client does not compute totals, they come from the API — and this page
 * publishes none, because a keyset page is a window rather than a set. The
 * profit and loss (FE051) is where the park's spending is added up, by the
 * server, over the whole period rather than over whatever fitted on one page.
 *
 * A correction and the expense it corrects **both** appear, neither hidden nor
 * netted away (R-707). The correction is marked as one and nothing here resolves
 * a chain: "there is no effective amount and no resolved chain — that would be a
 * stored derived value".
 */

/** Every state the list can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly items: readonly ExpenseResource[];
      readonly nextCursor: string | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The outlet filter's "not filtering" value. Not `''`, which Base UI clears to. */
const EVERY_OUTLET = 'every-outlet';

/** The vendor filter's "not filtering" value, for the same reason. */
const EVERY_VENDOR = 'every-vendor';

const COLUMNS = 7;

export function Expenses() {
  const client = useApiClient();

  // The period the *list* is being read over, and the period the pickers are
  // showing. They are the same value until somebody starts changing it: a range
  // half-cleared is not a request, so the last complete one stays on screen
  // while the new one is chosen.
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);

  const [vendorId, setVendorId] = useState<string>(EVERY_VENDOR);

  // Retired included: an expense paid to a vendor since retired is still one
  // somebody may want to find. A failed read costs the filter and nothing else.
  const [vendors, setVendors] = useState<readonly VendorResource[]>([]);

  const [trail, setTrail] = useState<CursorTrail>(FIRST_PAGE);

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  // The expense whose attachments are open. Held here rather than in its row,
  // so the dialog survives the row re-rendering under it.
  const [attachingTo, setAttachingTo] = useState<ExpenseResource | null>(null);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const problem = periodProblem(period, systemClock);

  const cursor = currentCursor(trail);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones are
    // included — an outlet retired last month still spent money the month
    // before, and a row whose outlet could not be named would read as a fault.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The filter and the outlet column are the only casualties, and the
        // list itself is unaffected. Not `outletsAnswered`: a failed read has
        // answered nothing about which outlets exist.
        if (live) setOutlets([]);
      });

    collectPages<VendorResource>((offset, limit) =>
      client.request(VendorsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setVendors(all);
      })
      .catch(() => {
        if (live) setVendors([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request. The notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (period.from === '' || period.to === '' || problem !== null) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(ExpensesController_list_v1, {
        query: {
          from: period.from,
          to: period.to,
          // The filter narrows within reach and never widens it, so "every
          // outlet" is the field omitted rather than a value meaning all.
          outletId: outletId === EVERY_OUTLET ? undefined : outletId,
          vendorId: vendorId === EVERY_VENDOR ? undefined : vendorId,
          // Sent back verbatim, and `undefined` on the first page. Opaque: never
          // constructed here.
          cursor: cursor ?? undefined,
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({ kind: 'loaded', items: answer.items, nextCursor: answer.nextCursor });
        }
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, period.from, period.to, outletId, vendorId, cursor, problem, reloads]);

  /**
   * A filter changed, so the trail is worthless.
   *
   * A cursor names a position in one ordered result set; carried into a
   * different one it is at best meaningless and at worst the out-of-range
   * instant `blossom-rest-api#347` answers a retryable 500 for. Every filter
   * change goes back to page one.
   */
  const changeFilter = (apply: () => void) => {
    apply();
    setTrail(FIRST_PAGE);
  };

  const outletsById = useMemo(
    () => new Map(outlets.map((outlet) => [outlet.id, outlet])),
    [outlets],
  );

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => [
      { label: 'Every outlet', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const vendorItems = useMemo(
    () => [
      { label: 'Every vendor', value: EVERY_VENDOR },
      ...vendors.map((vendor) => ({
        label: vendor.retiredAt === null ? vendor.name : `${vendor.name} (retired)`,
        value: vendor.id,
      })),
    ],
    [vendors],
  );

  const chosenOutlet = outlets.find((outlet) => outlet.id === outletId);

  const items = state.kind === 'loaded' ? state.items : [];

  const forbidden = state.kind === 'failed' && isForbidden(state.error);

  return (
    <Screen
      title="Expenses"
      action={
        <Button
          // `nativeButton={false}` because the rendered element is an anchor.
          // Base UI otherwise warns that native button semantics have been
          // removed, and it is right to: a link claiming to be a button is
          // announced as one and then does not answer the space bar.
          nativeButton={false}
          render={<Link to={paths.recordExpense} />}
        >
          <IconPlus data-icon="inline-start" />
          Record an expense
        </Button>
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="expenses-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="expenses-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => {
                  setPeriod((held) => ({ ...held, from: next }));
                });
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="expenses-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="expenses-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => {
                  setPeriod((held) => ({ ...held, to: next }));
                });
              }}
            />
          </div>

          {/* Under the pickers rather than above the table: it is about the two
              controls immediately above it, and it is not a failed load. */}
          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="expenses-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  // `null` is Base UI's "cleared", which this picker has no
                  // affordance for. Read as "not filtering" rather than cast
                  // away, so a cleared select can never become `outletId=null`
                  // in the query string.
                  setOutletId(next ?? EVERY_OUTLET);
                });
              }}
            >
              <SelectTrigger
                id="expenses-outlet"
                aria-label="Outlet"
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>Every outlet</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expenses-vendor" className="text-sm text-muted-foreground">
              Vendor
            </Label>
            <Select
              value={vendorId}
              items={vendorItems}
              onValueChange={(next) => {
                changeFilter(() => {
                  setVendorId(next ?? EVERY_VENDOR);
                });
              }}
            >
              <SelectTrigger
                id="expenses-vendor"
                aria-label="Vendor"
                disabled={vendors.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {vendorItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <ScreenFact label="Period">
            {period.from === '' || period.to === ''
              ? 'Choose both days.'
              : `${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}`}
          </ScreenFact>

          {chosenOutlet !== undefined && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{chosenOutlet.code}</span> — {chosenOutlet.name}
              {isRetired(chosenOutlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
            </ScreenFact>
          )}

          {/* No park-wide total and no sum of the column. This page has neither
              — see the note at the head of this file. */}
          {state.kind === 'loaded' && (
            <ScreenFact label="On this page">
              <span className="font-mono numeric">{items.length}</span>{' '}
              {items.length === 1 ? 'expense' : 'expenses'}
              {state.nextCursor !== null && (
                <>
                  <br />
                  <span className="text-muted-foreground">There are more pages.</span>
                </>
              )}
            </ScreenFact>
          )}
        </>
      }
    >
      <ScreenTable label="Expenses">
        <TableHeader>
          <TableRow>
            {/* Leads with the trading day because that is what the period filter
                matches on, and it is the server's answer rather than the clock
                of whoever typed the row. */}
            <TableHead className="w-44">Trading day</TableHead>
            <TableHead className="w-44">Outlet</TableHead>
            <TableHead>What for</TableHead>
            <TableHead className="w-44">Paid to</TableHead>
            <TableHead className="w-28">How paid</TableHead>
            {/* Amounts right-align, per docs/ui-conventions.md — comparing down a
                column is the task. */}
            <TableHead className="w-32 text-right">Amount</TableHead>
            <TableHead className="w-32">Receipts</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {forbidden ? (
              <EmptyState
                title="This account cannot read the expense log"
                description="If you are an owner or manager, sign out and back in."
              />
            ) : (
              <LoadFailed error={state.error} onRetry={() => setReloads((count) => count + 1)} />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (items.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="Nothing was spent in this period"
                description={
                  vendorId !== EVERY_VENDOR
                    ? 'Try every vendor, or widen the period.'
                    : outletId === EVERY_OUTLET
                      ? 'Widen the period, or record the first one.'
                      : 'Try every outlet, or widen the period.'
                }
                action={
                  <Button
                    variant="outline"
                    nativeButton={false}
                    render={<Link to={paths.recordExpense} />}
                  >
                    Record an expense
                  </Button>
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {/* In the order the API sent them, which is newest first by
                  `recordedAt` with the id breaking a tie. Nothing here sorts,
                  filters, nets a correction off, or adds a row. */}
              {items.map((expense) => (
                <ExpenseRow
                  key={expense.id}
                  expense={expense}
                  outlet={outletsById.get(expense.outletId)}
                  outletsAnswered={outletsAnswered}
                  onOpenAttachments={() => setAttachingTo(expense)}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {/* Not while the load failed: the notice above already says the list could
          not be read, and a pager under it offers to move through rows that are
          not there. */}
      {state.kind === 'loaded' && (
        <CursorPager
          trail={trail}
          rows={items.length}
          nextCursor={state.nextCursor}
          onTrailChange={setTrail}
          label="expenses"
          noun="expense"
        />
      )}

      {attachingTo !== null && (
        <ExpenseAttachmentsDialog
          expense={attachingTo}
          open
          onOpenChange={(open) => {
            if (!open) setAttachingTo(null);
          }}
          onAttached={() => {
            // The server's count, advanced by the one it just recorded, so the
            // badge is right without re-reading the page and losing the dialog.
            setState((held) =>
              held.kind !== 'loaded'
                ? held
                : {
                    ...held,
                    items: held.items.map((expense) =>
                      expense.id === attachingTo.id
                        ? { ...expense, attachmentCount: expense.attachmentCount + 1 }
                        : expense,
                    ),
                  },
            );
          }}
        />
      )}
    </Screen>
  );
}

/**
 * One expense.
 *
 * `reason` is free text a person wrote and is rendered as **data**, which is
 * what React does with a string child and what the document asks for in as many
 * words — "returned as data and never as markup". Nothing here interpolates it
 * into HTML.
 */
function ExpenseRow({
  expense,
  outlet,
  outletsAnswered,
  onOpenAttachments,
}: {
  expense: ExpenseResource;
  outlet: OutletResource | undefined;
  outletsAnswered: boolean;
  onOpenAttachments: () => void;
}) {
  // `?? null` because an API older than blossom-rest-api#512 omits both fields,
  // and the back office can deploy ahead of it. Absent reads as "none".
  const vendor = expense.vendor ?? null;
  const receiptNumber = expense.receiptNumber ?? null;
  // Absent from an API older than blossom-rest-api#513, which serves no
  // attachment routes either — so there is nothing to offer.
  const attachmentCount = expense.attachmentCount as number | undefined;

  return (
    <TableRow>
      <TableCell className="py-3.5 align-top">
        <span className="numeric">{formatBusinessDate(expense.businessDate)}</span>
        <br />
        {/* The other clock. The trading day is the server's answer to which day
            this belongs to; this is when the person said the money went out, and
            a Tuesday payment entered on Thursday shows as exactly that. */}
        <span className="text-xs text-muted-foreground">
          Went out {formatInstant(expense.occurredAt)}
        </span>
      </TableCell>

      <TableCell className="align-top">
        {outlet === undefined ? (
          // The outlets could not be read, or this one is outside what came
          // back. Said as an absence rather than rendered as a bare UUID.
          <span className="text-muted-foreground">
            {outletsAnswered ? 'An outlet not in reach' : 'Loading…'}
          </span>
        ) : (
          <>
            <span className="font-mono numeric">{outlet.code}</span>
            <br />
            <span className="text-xs text-muted-foreground">{outlet.name}</span>
          </>
        )}
      </TableCell>

      {/* `whitespace-normal`: the registry's `TableCell` sets `nowrap`, which is
          right for a code and catastrophic for a sentence — the cell sizes to
          the whole reason and drags the table past the viewport. */}
      <TableCell className="align-top whitespace-normal">
        {expense.reason}
        {expense.correctsExpenseId !== null && (
          <>
            <br />
            {/* Both rows appear and neither is presented as a modification of
                the other (R-707). This says which one this is; it does not
                resolve a chain or net an amount off, because there is no
                effective amount to publish. */}
            <span className="text-xs text-muted-foreground">
              Corrects an earlier expense at this outlet.
            </span>
          </>
        )}
      </TableCell>

      <TableCell className="align-top whitespace-normal">
        {/* The vendor's current name, which may be a retired vendor's: the
            expense still names who was paid. */}
        {vendor === null ? <span className="text-muted-foreground">No vendor</span> : vendor.name}
        {receiptNumber !== null && (
          <>
            <br />
            <span className="text-xs text-muted-foreground">
              Receipt <span className="font-mono numeric">{receiptNumber}</span>
            </span>
          </>
        )}
      </TableCell>

      <TableCell className="align-top">{labelForPaymentMode(expense.paymentMode)}</TableCell>

      <TableCell className="text-right align-top font-mono numeric">
        {formatPaise(expense.amountPaise)}
      </TableCell>

      <TableCell className="align-top">
        {attachmentCount === undefined ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenAttachments}
            aria-label={`Receipts and invoices, ${String(attachmentCount)} attached`}
          >
            <IconPaperclip data-icon="inline-start" />
            {attachmentCount === 0 ? (
              'Attach'
            ) : (
              <Badge variant="secondary" className="numeric">
                {attachmentCount}
              </Badge>
            )}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
